import { Injectable, effect, inject, untracked } from '@angular/core'

import { Store } from '@ngrx/store'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { DEFAULT_LANGUAGE, DEFAULT_LOCALE } from '@flowx/core-sdk'
import {
  StartDataDialogComponent,
  StartDataFromVersion,
} from '@flowx/designer/modules/process/components/start-data-dialog/start-data-dialog.component'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { FlxThemesLoaderService } from '@flowx/designer/modules/theme/services/themes-loader.service'
import { LocaleService } from '@flowx/designer/modules/ui-designer/services/locale.service'
import { InformationModalComponent } from '@flowx/designer/shared/components/information-modal-component/information-modal-component'
import {
  ApplicationResourceType,
  matchesEntryPoint,
} from '@flowx/designer/shared/entities/application.entity'
import { StartProcessService } from '@flowx/designer/shared/services/start-process.service'
import {
  selectAppId,
  selectAppVersionId,
  selectWorkspaceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'

/**
 * Run in the project header: launches the project's entry point. On Config that is the selected
 * version's, through the start-data dialog; on Runtime, the build the active policy serves, through
 * the same launch as the Projects list.
 *
 * Provide it, with `FlxThemesLoaderService`, on the component that hosts Run inside the project.
 */
@Injectable()
export class ProjectLaunchService {
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #startProcessService = inject(StartProcessService)
  #store = inject(Store)
  #flxModalService = inject(FlxModalService)
  #themeLoader = inject(FlxThemesLoaderService)
  #localeService = inject(LocaleService)

  #workspaceId = this.#store.selectSignal(selectWorkspaceId)
  #appId = this.#store.selectSignal(selectAppId)
  #appVersionId = this.#store.selectSignal(selectAppVersionId)

  /**
   * The version the store's settings were loaded for. The store keeps the previous version's settings
   * until the selected version's arrive, and the settings don't say which version they belong to, so
   * this notes the version shown each time they change.
   */
  #settingsVersionId: string | undefined

  constructor() {
    // An effect, not a linkedSignal: it has to note the version when the settings change, not when
    // Run is clicked, by which time the version may already be the next one.
    effect(() => {
      this.#applicationDetailsStore.applicationSettings()
      this.#settingsVersionId = untracked(this.#applicationDetailsStore.applicationVersion)?.id
    })
  }

  launchRuntime(): void {
    this.#startProcessService.startProcess({
      workspaceId: this.#workspaceId(),
      applicationId: this.#appId(),
      applicationName: this.#applicationDetailsStore.appName(),
    })
  }

  /**
   * Never launches a version other than the one selected, drafts included. Right after a version
   * switch it does nothing until that version's settings and entry points have arrived, rather than
   * start it from the previous version's.
   */
  async launchVersion(): Promise<void> {
    if (!this.#selectedVersionLoaded()) {
      return
    }

    const settings = this.#applicationDetailsStore.applicationSettings()?.appVersionSettings
    const entryPoint = settings?.entrypoints?.WEB ?? settings?.entrypoints?.MOBILE

    if (!entryPoint) {
      this.#showInformation(
        'This current version has no entry point set. Set a <strong>Process</strong> or <strong>UI Flow</strong> as the entry point.'
      )
      return
    }

    // A UI Flow runs from a build only; starting one from a version needs backend support.
    if (entryPoint.resType === ApplicationResourceType.UiFlow) {
      this.#showInformation(
        `This version's entry point is a <strong>UI Flow</strong>, which runs from a build. Switch to <strong>Runtime</strong> to run the build the active policy serves.`,
        'Run from Runtime'
      )
      return
    }

    await this.#themeLoader.init()
    const themeList = this.#themeLoader.themeList()

    const entryPointResource = Object.values(this.#applicationDetailsStore.entryPointsRx.value())
      .flat()
      .find(matchesEntryPoint(entryPoint))

    const fromVersion: StartDataFromVersion = {
      appVersionId: this.#appVersionId(),
      process: {
        resourceId: entryPointResource?.resourceId ?? entryPoint.resId,
        resourceName: entryPointResource?.resourceName ?? '',
      },
      themes: {
        list: themeList,
        selected:
          themeList.find(({ flowxUuid }) => flowxUuid === settings.themeId) ||
          this.#themeLoader.defaultTheme(),
      },
    }

    this.#flxModalService.show(
      StartDataDialogComponent,
      {
        closeOnBackdropClick: true,
        backdropClass: 'overlay-backdrop',
      },
      {
        fromVersion,
        locale: this.#localeService.selectedLocale() || DEFAULT_LOCALE,
        language: settings.languages?.find(({ isDefault }) => isDefault)?.code || DEFAULT_LANGUAGE,
      }
    )
  }

  /** Whether the settings and entry points in the store are the selected version's. */
  #selectedVersionLoaded(): boolean {
    return (
      this.#settingsVersionId === this.#appVersionId() &&
      this.#applicationDetailsStore.entryPointsRx.status() === 'resolved'
    )
  }

  #showInformation(message: string, title = 'Entry Point not set'): void {
    this.#flxModalService.show(
      InformationModalComponent,
      { backdropClass: 'overlay-backdrop' },
      {
        title,
        subtitle: `<i class="ph ph-folder"></i> ${this.#applicationDetailsStore.appName()}`,
        message,
      }
    )
  }
}
